import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ClipboardList, Plus } from 'lucide-react'

import { STOCKTAKE_KINDS, STOCKTAKE_KIND_LABELS } from './-stocktake-kinds'
import type {
    StocktakeKind,
    StocktakeSessionTime,
    StocktakeStatus,
} from '@/lib/api/stocktake-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    STOCKTAKE_CARD_CODE_CLASS,
    StocktakeListCard,
} from '@/components/common/StocktakeListCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useStocktakeSessions,
    useStocktakeSessionsInfinite,
} from '@/hooks/use-stocktake-query'
import { formatDateVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const KIND_LABELS = STOCKTAKE_KIND_LABELS

const STATUS_LABELS: Record<StocktakeStatus, () => string> = {
    draft: () => m.stocktake_status_draft(),
    counting: () => m.stocktake_status_counting(),
    pending_approval: () => m.stocktake_status_pending_approval(),
    approved: () => m.stocktake_status_approved(),
    cancelled: () => m.stocktake_status_cancelled(),
}

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Cả ba trước đây nằm trong `useState` nên không vào URL (R9).
 */
const FILTERS = defineListFilters({
    kind: selectFilter<StocktakeKind | 'all'>({
        label: m.stocktake_filter_kind_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.stocktake_filter_all_kinds() },
            ...STOCKTAKE_KINDS.map((k) => ({
                value: k,
                label: KIND_LABELS[k](),
            })),
        ],
    }),
    status: selectFilter<StocktakeStatus | 'all'>({
        label: m.stocktake_filter_status_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.stocktake_filter_all_status() },
            ...(
                [
                    'draft',
                    'counting',
                    'pending_approval',
                    'approved',
                    'cancelled',
                ] as StocktakeStatus[]
            ).map((k) => ({ value: k, label: STATUS_LABELS[k]() })),
        ],
    }),
    session_time: selectFilter<StocktakeSessionTime | 'all'>({
        label: m.stocktake_filter_session_time_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.stocktake_filter_all_session_times() },
            { value: 'evening', label: m.stocktake_session_time_evening() },
            { value: 'morning', label: m.stocktake_session_time_morning() },
        ],
    }),
})

export const Route = createFileRoute('/_app/_authed/_sidebar/stocktake/')({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

const STATUS_COLORS: Record<StocktakeStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    counting: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
}

function Page() {
    const navigate = useNavigate()
    const { values, barProps } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                to: '/stocktake',
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    const isMobile = useIsMobile()

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.kind !== 'all' ? { kind: values.kind } : {}),
            ...(values.status !== 'all' ? { status: values.status } : {}),
            ...(values.session_time !== 'all'
                ? { session_time: values.session_time }
                : {}),
        }),
        [values.kind, values.status, values.session_time],
    )

    // Màn này chưa có thanh phân trang desktop, luôn lấy trang 1.
    const params = useMemo(
        () => ({ ...apiFilters, page: 1, per_page: 50 }),
        [apiFilters],
    )

    const { data, isLoading, isFetching, refetch } = useStocktakeSessions(
        params,
        { enabled: !isMobile },
    )
    const items = data?.items ?? []

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useStocktakeSessionsInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileItems = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.items ?? []) ?? [],
        [infinite.data],
    )

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.stocktake_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<ClipboardList className="size-5 shrink-0" />}
                title={m.stocktake_page_title()}
                subtitle={m.stocktake_page_subtitle()}
                primaryActions={
                    <Button
                        onClick={() => void navigate({ to: '/stocktake/new' })}
                        className="w-full"
                    >
                        <Plus className="size-4 mr-1" />
                        {m.stocktake_new_button()}
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : mobileItems.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileItems.map((s) => (
                            <StocktakeListCard
                                key={s.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/stocktake/$id',
                                        params: { id: s.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/stocktake/$id"
                                        params={{ id: s.id }}
                                        className={STOCKTAKE_CARD_CODE_CLASS}
                                    >
                                        {s.code}
                                    </Link>
                                }
                                kindBadge={
                                    <Badge variant="outline">
                                        {KIND_LABELS[s.kind]()}
                                    </Badge>
                                }
                                statusBadge={
                                    <Badge className={STATUS_COLORS[s.status]}>
                                        {STATUS_LABELS[s.status]()}
                                    </Badge>
                                }
                                sessionDate={s.session_date}
                                totalLines={s.total_lines}
                                varianceLines={s.variance_lines}
                                createdBy={s.created_by_name ?? undefined}
                                approvedBy={s.approved_by_name ?? undefined}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.stocktake_col_code()}</TableHead>
                            <TableHead>{m.stocktake_col_kind()}</TableHead>
                            <TableHead>{m.stocktake_col_date()}</TableHead>
                            <TableHead>
                                {m.stocktake_col_session_time()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.stocktake_col_total_lines()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.stocktake_col_variance()}
                            </TableHead>
                            <TableHead>{m.stocktake_col_status()}</TableHead>
                            <TableHead>{m.stocktake_col_creator()}</TableHead>
                            <TableHead>{m.stocktake_col_approver()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            items.map((s) => (
                                <TableRow
                                    key={s.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/stocktake/$id',
                                            params: { id: s.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono">
                                        <Link
                                            to="/stocktake/$id"
                                            params={{ id: s.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {s.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {KIND_LABELS[s.kind]()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        {formatDateVN(s.session_date)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant="outline"
                                            className={
                                                s.session_time === 'morning'
                                                    ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                                                    : 'border-blue-200 text-blue-700 bg-blue-50'
                                            }
                                        >
                                            {s.session_time === 'morning'
                                                ? m.stocktake_session_time_short_morning()
                                                : m.stocktake_session_time_short_evening()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {s.total_lines}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {s.variance_lines > 0 ? (
                                            <span className="text-red-700 font-semibold">
                                                {s.variance_lines}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">
                                                —
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            className={STATUS_COLORS[s.status]}
                                        >
                                            {STATUS_LABELS[s.status]()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {s.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {s.approved_by_name ?? '—'}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
